import * as Highcharts from 'highcharts/highstock';
import { sortBy } from "lodash";
import { format, parse, compareAsc, getUnixTime, add, nextMonday, subDays, startOfMonth } from "date-fns";
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { MoodCount, MoodsByDay, MoodsByDayReduced, MoodsByTimeRange } from './utils.type';

@Injectable({
    providedIn: 'root'
})
export class GraphUtils {

    constructor(
        private translateService: TranslateService,
    ) { 
        // the download icon on the graphs
        Highcharts.SVGRenderer.prototype.symbols.download = function (x, y, w, h) {
            return [
                // Arrow stem
                'M', x + w * 0.5, y,
                'L', x + w * 0.5, y + h * 0.7,
                // Arrow head
                'M', x + w * 0.3, y + h * 0.5,
                'L', x + w * 0.5, y + h * 0.7,
                'L', x + w * 0.7, y + h * 0.5,
                // Box
                'M', x, y + h * 0.9,
                'L', x, y + h,
                'L', x + w, y + h,
                'L', x + w, y + h * 0.9
            ];
        };
        
        Highcharts.setOptions({
            time:{
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            },
            lang: {
                months: [
                    this.translateService.instant('january'),
                    this.translateService.instant('february'),
                    this.translateService.instant('march'),
                    this.translateService.instant('april'),
                    this.translateService.instant('may'),
                    this.translateService.instant('june'),
                    this.translateService.instant('july'),
                    this.translateService.instant('august'),
                    this.translateService.instant('september'),
                    this.translateService.instant('october'),
                    this.translateService.instant('november'),
                    this.translateService.instant('december'),
                ]
            },
            credits: {
                text: 'TeamMood.com',
                href: 'https://www.teammood.com'
            },
            exporting: {
                enabled: false
            }
        });
    }

    colours (value: string): string {
        return coloursHex[value];
    };

    graphBackgroundColor = "#FFFFFF"

    scoreToColor (score: number): string {
        switch (true) {
            case (score <= 2):
                return coloursHex['bad'];
            case (score <= 4):
                return coloursHex['hard'];
            case (score <= 6):
                return coloursHex['average'];
            case (score <= 8):
                return coloursHex['good'];
            case (score <= 10):
                return coloursHex['excellent'];
        }
    };

    moods_total (mood_count: MoodCount): number {
        return mood_count.excellent +
            mood_count.good +
            mood_count.average +
            mood_count.hard +
            mood_count.bad;
    };

    plotValues(mood: string): number {
        switch (mood) {
            case 'excellent':
                return 9;
            case 'good':
                return 7;
            case 'average':
                return 5;
            case 'hard':
                return 3;
            case 'bad':
                return 1;
            default :
                return 5;
        }
    }

    moodToValue (mood: string): number {
        switch (mood) {
            case 'excellent':
                return 10;
            case 'good':
                return 7.5;
            case 'average':
                return 5;
            case 'hard':
                return 2.5;
            case 'bad':
                return 0;
            default :
                return 5;
        }
    };

    scoreToMood (note: number): string {
        let mood;
        switch (true) {
            case (note <= 2):
                mood = 'bad';
                break;
            case (note <= 4):
                mood = 'hard';
                break;
            case (note <= 6):
                mood = 'average';
                break;
            case (note <= 8):
                mood = 'good';
                break;
            case (note <= 10):
                mood = 'excellent';
                break;
        }
        return mood;
    };

    addS (number: number): string { return number > 1 ? 's' : '' }
    epoch (date: string): number {
        return getUnixTime(parse(date, 'dd-MM-yyyy', new Date()))*1000;
    }

    wordwrap(str: string, width: number, brk: string, cut?: boolean): string {
        brk = brk || 'n';
        width = width || 75;
        cut = cut || false;

        if (!str || str.trim().length < 1) {
            return str;
        }
        const regex = '.{1,' + width + '}(\\s|$)' + (cut ? '|.{' + width + '}|.+$' : '|\\S+?(\\s|$)');

        return str.match(new RegExp(regex, 'g')).join(brk);
    }

    // duplicate code from mood-distrubtion-by-day.component.ts to keep the tests (loose this is undefined error)
    groupByTimeRange (mood_by_day: MoodsByDay[], timeRange: string ='day'): MoodsByTimeRange[] {
        if(mood_by_day && mood_by_day.length > 0){
            mood_by_day = sortBy(mood_by_day, mood => getUnixTime(parse(mood.date, 'dd-MM-yyyy', new Date())));

            if (timeRange === 'day') {
                return mood_by_day;
            }

            const firstDayOfSelection = parse(mood_by_day[0].date,  'dd-MM-yyyy', new Date());
            const lastDayOfSelection = parse(mood_by_day[mood_by_day.length-1].date,  'dd-MM-yyyy', new Date());
            let dateIntervals;

            if(timeRange === 'week'){
                dateIntervals = this.createDateInterval(firstDayOfSelection, lastDayOfSelection, this.findNextMonday);
            }
            if(timeRange === 'month'){
                dateIntervals = this.createDateInterval(firstDayOfSelection, lastDayOfSelection, this.findFirstDayOfNextMonth);
            }            

            const moodByDayByInterval = dateIntervals.map(interval =>{
                return {
                    interval: { start: interval.start, end: interval.end},
                    moods: mood_by_day.filter(day => {
                        return this.isDateInsideDateRange(day.date, interval.start, interval.end);
                    })
                }
            })

            let moodByDayByIntervalReduced = moodByDayByInterval.map(subObj => {
                if(subObj.moods.length > 0){
                    return this.reduceArray(subObj.moods, subObj.interval.start, subObj.interval.end);
                }
            });
            moodByDayByIntervalReduced = moodByDayByIntervalReduced.filter(mood => mood !== undefined);
            return moodByDayByIntervalReduced;
        }else{
            return [];
        }
    }

    isDateInsideDateRange(date: string, start: string, end: string): boolean{
        const dateFNS = parse(date, 'dd-MM-yyyy', new Date());
        const startFNS = parse(start, 'dd-MM-yyyy', new Date());
        const endFNS = parse(end, 'dd-MM-yyyy', new Date());

        const dateIsAfterStart = compareAsc(startFNS, dateFNS);
        const dateIsBeforeEnd = compareAsc(dateFNS ,endFNS);

        if(dateIsAfterStart <= 0 && dateIsBeforeEnd <= 0){
            return true;
        }else{
            return false;
        }
    }

    findNextMonday(date: Date): Date {
        return nextMonday(date);
    }

    findFirstDayOfNextMonth(date: Date): Date {
        return startOfMonth(add(date, {months: 1}));
    }

    createDateInterval(start: Date, end: Date, findFirstDayOfNextInterval: (date: Date) => Date): Array<{start: string, end: string}> {
        const intervals = [];
        for (let i = start; compareAsc(i ,end) <= 0; i = findFirstDayOfNextInterval(i)) {
            const nextEndDayOfInterval = subDays(findFirstDayOfNextInterval(i), 1);
            const currentEnd = compareAsc(end, nextEndDayOfInterval) <= 0 ? end : nextEndDayOfInterval;
            intervals.push({
                start: format(i, 'dd-MM-yyyy'),
                end: format(currentEnd, ('dd-MM-yyyy'))
            });
        }
        return intervals;
    }

    reduceArray(arr: MoodsByDay[], start: string, end: string): MoodsByDayReduced {
        if(arr.length > 0){
            let concatComments = [];
            let average = 0;
            let good = 0;
            let bad = 0;
            let hard = 0;
            let excellent = 0;

            for(let i=0; i<arr.length; i++){
                average += arr[i].moodCount.average;
                good += arr[i].moodCount.good;
                bad += arr[i].moodCount.bad;
                hard += arr[i].moodCount.hard;
                excellent += arr[i].moodCount.excellent;

                if(arr[i].comments){
                    concatComments = [...concatComments, ...arr[i].comments];
                }
            }

            const endDateContent = '- '+ format(parse(end, 'dd-MM-yyyy', new Date()), "E dd MMM");

            return {
                comments: concatComments,
                date: start,
                endDate: endDateContent,
                moodCount: {
                    average: average,
                    good: good,
                    bad: bad,
                    hard: hard,
                    excellent: excellent,
                },
            }
        }
    }
}

const coloursHex = {
    excellent: "#1abc9c",
    good: "#5c91df",
    average: "#f1c40f",
    hard: "#ff7a23",
    bad: "#b33c6b"
};